/**
 * Русская локаль.
 *
 * Типизирована по узбекской: `uz-Latn` — язык по умолчанию (FR-558), и именно он
 * задаёт состав ключей. Пропущенный ключ не соберётся — расхождение состава между
 * локалями обнаруживается компилятором, а не пользователем, увидевшим пустое место.
 */
export const ru = {
    common: {
        next: 'Далее',
        back: 'Назад',
        save: 'Сохранить',
        skip: 'Пропустить',
        cancel: 'Отмена',
        retry: 'Повторить',
        close: 'Закрыть',
        done: 'Готово',
        call: 'Позвонить',
        copy: 'Копировать',
        openSettings: 'Открыть настройки',
        offline: 'Нет сети',
        online: 'В сети',
        minutesShort: 'мин',
        hoursShort: 'ч',
        queued: 'в очереди',
    },
    onboarding: {
        languageTitle: 'Выберите язык',
        languageSubtitle: 'Язык можно сменить позже в настройках',
        locationTitle: 'Геолокация',
        locationWhy: 'Чтобы диспетчер видел вас на маршруте, а клиент — время прибытия',
        locationRequirement: 'Нужно разрешение уровня «Всегда»',
        notificationsTitle: 'Уведомления',
        notificationsWhy: 'Чтобы новые заказы и обращения диспетчера приходили сразу',
        batteryTitle: 'Энергосбережение',
        batteryWhy: 'Чтобы система не выгружала приложение и трекинг не прерывался',
        loginTitle: 'Вход',
        loginPhone: 'Номер телефона',
        loginCode: 'Код из СМС',
        loginRequestCode: 'Получить код',
        loginVerify: 'Войти',
    },
    shift: {
        closedTitle: 'Смена закрыта',
        start: 'Начать смену',
        finish: 'Завершить смену',
        mode: 'Режим',
        zone: 'Зона',
        vehicle: 'Транспорт',
        equipment: 'Доступная тара',
        baseLocation: 'Точка возврата',
        batteryWarning: 'Низкий заряд: трекинг может прерваться',
        gpsLabel: 'GPS',
        gpsGood: 'точно',
        gpsWeak: 'слабо',
        gpsNone: 'нет сигнала',
        queueLabel: 'Очередь',
        batteryLabel: 'Заряд',
        blockedNoBackgroundLocation: 'Фоновая геолокация не включена. Разрешите доступ «Всегда», чтобы открыть смену.',
        warnNotifications: 'Уведомления отключены: вы не заметите новый заказ',
        warnBatterySaver: 'Включено энергосбережение: приложение может выгружаться',
        blockedGoodsWithCourier: 'Есть несданный товар. Сначала верните его в точку отправления.',
        blockedStopsPending: 'Задание не отработано: закройте все точки',
        modeExpress: 'Курьерская служба',
        modeDistribution: 'Дистрибуция',
        pickupLocation: 'Точка отправления',
        openTitle: 'Открытие смены',
        chooseMode: 'Выберите режим работы',
        chooseLocation: 'Выберите точку отправления',
        chooseZone: 'Выберите зону',
        loadingOptions: 'Загружаем варианты…',
        noModes: 'Вашей учётной записи не назначен ни один отдел. Обратитесь к диспетчеру.',
        noLocations: 'Нет доступных точек отправления. Обратитесь к диспетчеру.',
        locationWithoutZone: 'Точка не входит ни в одну зону. Диспетчер должен привязать её к зоне.',
        optionsFailed: 'Не удалось загрузить варианты смены',
        openRefusedMode: 'Этот режим недоступен вашей учётной записи',
        openRefusedAlreadyOpen: 'Смена уже открыта',
        openRefusedNoLocation: 'Не выбрана точка отправления',
        openRefusedInvalid: 'Сервер не принял данные смены. Обратитесь к диспетчеру.',
        openFailed: 'Не удалось открыть смену',
        finishRefusedAlreadyFinished: 'Смена уже завершена',
        finishFailed: 'Не удалось завершить смену',
        commandOffline: 'Нет связи. Открыть или завершить смену можно только онлайн.',
    },
    task: {
        title: 'Задание',
        etaNext: 'До следующей точки',
        etaComplete: 'Завершение задания',
        etaReturn: 'Возврат на базу',
        empty: 'Точек пока нет',
        currentLeg: 'Текущее плечо',
        changed: 'Задание изменилось: проверьте порядок точек',
        arrivalEta: 'Прибытие',
        routeNavigate: 'Весь маршрут в навигаторе',
        planOpen: 'Изменить порядок',
        planTitle: 'Порядок точки',
        planMoveFirst: 'Первым',
        planMoveLast: 'Последним',
        planMoveEarlier: 'Раньше',
        planMoveLater: 'Позже',
        planTargetAt: 'Привезти к…',
        planTargetLabel: 'Привезти к',
        planTargetPlaceholder: 'ЧЧ:ММ',
        planTargetClear: 'Сбросить время',
        planTargetHint: 'Время снимает ваш ручной порядок: рейс перестроится по срокам',
        planPinned: 'Порядок задан вами',
        planOffline: 'Для изменения порядка нужна связь',
        planNotAllowed: 'В этом задании менять маршрут нельзя',
        planStopNotMovable: 'Эту точку переставить нельзя',
        planInvalid: 'Неверное значение',
        planFailed: 'Не удалось изменить, повторите',
        planTimeInvalid: 'Введите время в виде ЧЧ:ММ',
        planTimePassed: 'Это время сегодня уже прошло',
    },
    stop: {
        typePickup: 'Забор',
        typeDropoff: 'Выдача',
        typeReturn: 'Возврат',
        entrance: 'Подъезд',
        floor: 'Этаж',
        apartment: 'Квартира',
        doorPhone: 'Домофон',
        doorPhoneCopied: 'Код домофона скопирован',
        comment: 'Комментарий',
        recipient: 'Получатель',
        items: 'Состав',
        cargoCategory: 'Категория груза',
        requiredEquipment: 'Требуемая тара',
        navigate: 'Навигация',
        arrived: 'Я на месте',
        pickedUp: 'Забрал',
        pickupFailed: 'Товар не выдали',
        delivered: 'Доставлено',
        failed: 'Не доставил',
        handedOver: 'Сдал',
        reasonRequired: 'Выберите причину',
        reasonsUnavailable: 'Справочник причин не загружен',
        groupNumber: 'Номер заказа',
        tmsNumber: 'Номер TMS',
        deadline: 'Дедлайн',
        nextStop: 'Следующая точка',
    },
    offer: {
        title: '+1 заказ',
        increase: 'Прирост времени',
        newComplete: 'Новое время завершения',
        accept: 'Принять',
        decline: 'Отказаться',
        declineReason: 'Причина отказа',
        pickup: 'Адрес забора',
        dropoff: 'Адрес выдачи',
        deadline: 'Дедлайн',
        expiresIn: 'Время на ответ',
        expired: 'Время вышло: заказ ушёл другому курьеру',
    },
    messages: {
        title: 'Обращения',
        reply: 'Ответить',
        empty: 'Обращений нет',
    },
    diagnostics: {
        title: 'Диагностика',
        fix: 'Исправить',
        permissionGranted: 'Выдано',
        permissionDenied: 'Не выдано',
        sendLog: 'Отправить журнал',
        logQueued: 'Журнал поставлен в очередь на отправку',
        backgroundLocation: 'Фоновая геолокация',
        notifications: 'Уведомления',
        batterySaver: 'Режим энергосбережения',
        batteryGuide: 'Инструкция по энергосбережению',
    },
    sync: {
        pending: 'Неотправленных действий',
        conflict: 'Задание изменилось: действие не применено',
        duplicate: 'Действие уже принято',
    },
    languages: {
        uzLatn: "O'zbekcha",
        ru: 'Русский',
    },
    report: {
        title: 'Отчёт смены',
        workedTime: 'Время работы',
        runs: 'Рейсы',
        stops: 'Точки',
        delivered: 'Доставлено',
        notDelivered: 'Не доставлено',
        returns: 'Возвраты',
        distance: 'Пробег',
        etaPlanFact: 'ETA план/факт',
        returnPlanFact: 'Возврат на базу план/факт',
    },
    zone: {
        title: 'Моя зона',
        homePoint: 'Домашняя точка',
        proposeHome: 'Предложить другую домашнюю точку',
        proposalPending: 'Предложение ждёт подтверждения администратора',
        assignedZones: 'Закреплённые зоны',
        pickupLocations: 'Точки отправления',
        mapUnavailable: 'Карта пока недоступна',
    },
    history: {
        title: 'История',
        empty: 'Завершённых смен нет',
    },
    settings: {
        title: 'Настройки',
        language: 'Язык',
        sound: 'Звук',
        vibration: 'Вибрация',
    },
    /**
     * Подписи статусов — единая таблица (TMS_WEB_CABINET_UX.md, разд. 2.3.1).
     *
     * Приложение и кабинет диспетчера используют одни и те же слова: расхождение
     * подписей между экранами читается как разные сущности.
     */
    status: {
        new: 'Новый',
        assigned: 'Назначен',
        picked_up: 'Забрал заказ',
        in_transit: 'В пути',
        delivered: 'Доставлен',
        not_delivered: 'Не доставлен',
        unassigned: 'Без исполнителя',
        cancelled: 'Отменён',
        returned: 'Возвращён в CT',
        resolved: 'Решение принято',
        goodsWithCourier: 'Товар у курьера',
        returnStop: 'Возврат товара',
        atRisk: 'Дедлайн под угрозой',
    },
    /**
     * Кабинет — рабочее место диспетчера (02-ui/TMS_WEB_CABINET_UX.md).
     *
     * Подписи статусов здесь не дублируются: они в разделе `status`, в единой таблице
     * (разд. 2.3.1). Расхождение подписей между экранами читается как разные сущности.
     */
    cabinet: {
        period: {
            months: [
                'Январь',
                'Февраль',
                'Март',
                'Апрель',
                'Май',
                'Июнь',
                'Июль',
                'Август',
                'Сентябрь',
                'Октябрь',
                'Ноябрь',
                'Декабрь',
            ],
            monthsOf: [
                'января',
                'февраля',
                'марта',
                'апреля',
                'мая',
                'июня',
                'июля',
                'августа',
                'сентября',
                'октября',
                'ноября',
                'декабря',
            ],
            weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
            dayMonth: '{day} {month}',
            label: 'Период',
            today: 'Сегодня',
            yesterday: 'Вчера',
            week: 'Эта неделя',
            month: 'Этот месяц',
            all: 'Всё время',
            reset: 'Сбросить',
            days: 'дн.',
            previousMonth: 'Предыдущий месяц',
            nextMonth: 'Следующий месяц',
        },
        nav: {
            monitoring: 'Мониторинг',
            flow: 'Доставки',
            live_map: 'Карта',
            resolutions: 'Разбор',
            returns: 'Товар у курьера',
            import: 'Импорт CSV',
            locations: 'Локации',
            zones: 'Зоны',
            drivers: 'Курьеры',
            pickup_matrix: 'Матрица закреплений',
            integrations: 'Интеграции',
            feature_flags: 'Флаги',
            parameters: 'Параметры',
            slo: 'SLO',
            audit: 'Журнал аудита',
            runs: 'Рейсы',
            templates: 'Шаблоны СМС',
            users: 'Пользователи',
        },
        /** Отделы единой инсталляции — режимы доставки (ADR-0030). */
        modes: {
            express: 'Курьерская служба',
            distribution: 'Дистрибуция',
        },
        users: {
            name: 'ФИО',
            phone: 'Телефон',
            role: 'Роль',
            departments: 'Отделы',
            noDepartment: 'Без отдела — данных не видит',
            saveFailed: 'Не удалось сохранить отделы',
            filter: {
                all: 'Все',
                admin: 'Администраторы',
                dispatcher: 'Логисты',
                driver: 'Курьеры',
            },
            roles: {
                admin: 'Администратор',
                dispatcher: 'Логист',
                driver: 'Курьер',
            },
        },
        auth: {
            lead: 'Доступ только для сотрудников службы доставки.',
            note: 'Доступ выдаёт администратор службы.',
            title: 'Вход в кабинет TMS',
            phone: 'Номер телефона',
            phoneExample: '+998 90 123 45 67',
            password: 'Пароль',
            submit: 'Войти',
            failed: 'Номер телефона или пароль не подходят',
            locked: 'Вход временно закрыт: превышено число попыток',
        },
        flow: {
            columnAmount: 'Сумма',
            columnClient: 'Клиент',
            columnCourier: 'Курьер',
            details: 'подробнее',
            currency: 'сум',
            stagesLabel: 'Этапы доставки',
            stages: {
                accepted: 'Принят',
                assigned: 'Назначен курьер',
                pickedUp: 'Забрал',
                inTransit: 'В пути',
                delivered: 'Вручён',
            },
            found: 'найдено',
            searchPlaceholder: 'Номер, адрес, получатель или курьер',
            columnDepartment: 'Отдел',
            columnAddress: 'Адрес',
            columnRecipient: 'Получатель',
            columnDue: 'Срок',
            columnState: 'Состояние',
            columnAction: 'Действие',
            open: 'Открыть',
            courier: 'курьер',
            lateBy: 'опаздывает на',
            overdueBy: 'просрочен на',
            etaAt: 'прибытие',
            title: 'Доставки',
            create: 'Ручной ввод',
            import: 'Импорт CSV',
            chips: {
                attention: 'Требуют внимания',
                finished: 'Завершённые',
                all: 'Все',
                at_risk: 'Под угрозой',
                unassigned: 'Без исполнителя',
                triage: 'В разборе',
                goods: 'Товар у курьера',
                new: 'Новые',
                active: 'В работе',
                done: 'Выполненные',
                deferred: 'Отложенные',
                returned: 'Возвращённые',
                cancelled: 'Отменённые',
            },
            columnNumber: 'Заказ',
            columnStatus: 'Статус',
            columnRoute: 'Маршрут',
            columnDeadline: 'Дедлайн',
            columnEta: 'Прогноз',
            columnDriver: 'Водитель',
            columnPickup: 'Точка отправления',
            noDriver: 'не назначен',
            more: 'Ещё',
            shown: 'Показано',
            newOrders: ['новый заказ', 'новых заказа', 'новых заказов'],
        },
        order: {
            title: 'Заказ',
            planFact: 'План / факт',
            shouldArrive: 'должен был',
            arrived: 'приехал',
            pickedUp: 'забрал',
            handedOver: 'отдал',
            driver: 'Водитель',
            equipment: 'Тара',
            cargoCategory: 'Категория груза',
            mode: 'Режим',
            items: 'Состав заказа',
            attempts: 'Попытки доставки',
            resolutions: 'Решения',
            ctMessages: 'Обращения CT',
            changeLog: 'Журнал изменений',
            recipient: 'Получатель',
            driverPhoneNote: 'Телефон курьера получателю не передаётся',
            coordinates: 'Координаты',
            coordinatesMissing: 'Адрес не найден на карте — задайте точку вручную',
            coordinatesManual: 'Заданы вручную',
            latitude: 'Широта',
            longitude: 'Долгота',
            saveCoordinates: 'Сохранить координаты',
            zone: 'Зона',
            noZone: 'Вне зон — автоматически не назначается',
            copy: 'Копировать',
        },
        assignment: {
            title: 'Назначение исполнителя',
            recommended: 'рекомендуется',
            load: 'загрузка',
            increase: 'прирост к заданию',
            willDeliver: 'доставит к',
            deadline: 'дедлайн',
            deadHead: 'подача',
            assign: 'Назначить',
            excluded: 'Исключены',
            force: 'Принудительное назначение',
            forceReason: 'Причина принудительного назначения',
            empty: 'Подходящих курьеров нет',
            unknownDriver: 'Курьер',
            exclusions: {
                no_equipment: 'нет нужной тары',
                not_attached_to_location: 'не закреплён за точкой',
                max_orders_reached: 'достигнут предел заказов',
                deadline_violated: 'не успеет к сроку',
                out_of_zone: 'вне своей зоны',
                no_route: 'маршрут не построен',
            },
        },
        resolutions: {
            title: 'Разбор недоставок',
            waiting: 'ожидает',
            reason: 'Причина от водителя',
            ctHandled: 'Решение принято ИИ-контуром CT',
            decisionCancel: 'Отменить',
            decisionReschedule: 'Перенести',
            decisionReassign: 'Переназначить',
            returnToCt: 'Вернуть в CT',
            newDeadline: 'Новый дедлайн',
            empty: 'Заказов в разборе нет',
        },
        returns: {
            title: 'Товар у курьера',
            byDriver: 'По водителю',
            returnTo: 'Точка возврата',
            positions: 'позиций',
            empty: 'Несданного товара нет',
        },
        create: {
            title: 'Ручной ввод заказа',
            department: 'Отдел',
            sectionFrom: 'Откуда',
            sectionTo: 'Куда',
            sectionCargo: 'Груз',
            sectionTerms: 'Условия',
            pickupLocation: 'Точка отправления',
            address: 'Адрес',
            entrance: 'Подъезд',
            floor: 'Этаж',
            apartment: 'Квартира',
            doorPhone: 'Домофон',
            recipientName: 'Получатель',
            recipientPhone: 'Телефон',
            comment: 'Комментарий',
            cargoCategory: 'Категория груза',
            items: 'Состав',
            equipment: 'Требуемая тара',
            deadline: 'Дедлайн',
            doorToDoor: 'До двери',
            consent: 'Согласие на СМС',
            itemsHint: 'По одной позиции на строку',
            language: 'Язык уведомлений',
            checkFeasibility: 'Проверить выполнимость',
            submit: 'Создать заказ',
            feasible: 'Выполнимо',
            infeasible: 'Невыполнимо',
            earliest: 'Ближайшее достижимое время',
            availableCouriers: 'Свободных курьеров',
        },
        importCsv: {
            title: 'Импорт CSV',
            choose: 'Выберите файл',
            preview: 'Предпросмотр',
            apply: 'Применить',
            rows: 'строк',
            valid: 'корректных',
            invalid: 'с ошибками',
            dryRunNote: 'Сначала предпросмотр: применяются только корректные строки',
            errors: {
                empty_file: 'файл пуст',
                missing_column: 'нет обязательной колонки',
                invalid: 'неверное значение',
                rejected: 'заказ отклонён',
                isNotEmpty: 'пустое значение',
                matches: 'неверный формат',
                out_of_zone: 'адрес вне зоны доставки',
                unknown_location: 'неизвестная точка отправления',
                missing_location: 'не указана точка отправления',
                invalid_data: 'неверные данные',
                deadline_unreachable: 'срок недостижим',
            },
        },
        directories: {
            locations: 'Точки отправления',
            zones: 'Зоны',
            drivers: 'Водители',
            externalCode: 'Внешний код',
            name: 'Название',
            type: 'Тип',
            address: 'Адрес',
            active: 'Активна',
            readOnly: 'Только просмотр',
            locationTypes: {
                warehouse: 'Склад',
                pharmacy: 'Аптека',
                store: 'Магазин',
            },
        },
        matrix: {
            title: 'Точка отправления × водители',
            ordersInProgress: ['заказ в работе', 'заказа в работе', 'заказов в работе'],
            driversOnShift: ['водитель на смене', 'водителя на смене', 'водителей на смене'],
            avgForecast: 'средний прогноз',
            apply: 'Применить',
            attached: 'закреплён',
        },
        ops: {
            byMode: 'Значения отделов',
            integrations: 'Интеграции',
            state: 'Состояние',
            lastSuccess: 'Последний успешный обмен',
            errors24h: 'Ошибок за сутки',
            queue: 'Очередь',
            dlq: 'Не доставлено, ждут разбора',
            sloNames: {
                express_delivered_before_deadline: 'Доля экспресс-заказов, доставленных до дедлайна',
                events_delivered_first_try: 'Доля событий, доставленных в CT с первой попытки',
                orders_assigned_in_15m: 'Доля заказов, назначенных в течение 15 минут',
                orders_returned_share: 'Доля заказов, возвращённых оркестратору',
            },
            retry: 'Отправить повторно',
            flags: 'Флаги',
            parameters: 'Параметры',
            value: 'Значение',
            range: 'Диапазон',
            slo: 'SLI / SLO',
            target: 'Цель',
            current: 'Текущее',
            breached: 'нарушен',
            audit: 'Журнал аудита',
            actor: 'Автор',
            action: 'Действие',
            entity: 'Объект',
            integrationNames: {
                ct_webhook: 'Отправка событий в CT',
            },
            states: {
                ok: 'Работает',
                degraded: 'С перебоями',
                failed: 'Не работает',
            },
        },
        runs: {
            title: 'Рейсы и задания',
            pickDriver: 'Выберите курьера',
            run: 'Рейс',
            revision: 'Версия',
            distance: 'Пробег',
            duration: 'Длительность',
            complete: 'Завершение',
            returnAt: 'Возврат на базу',
            sequence: '№',
            type: 'Тип',
            order: 'Заказ',
            address: 'Адрес',
            plan: 'План',
            fact: 'Факт',
            window: 'Окно',
            up: 'Выше',
            down: 'Ниже',
            save: 'Сохранить порядок',
            replan: 'Пересчитать',
            reassign: 'Другому курьеру',
            pinned: 'Вручную',
            locked: 'Текущее плечо',
            late: 'Опоздание к окну',
            wait: 'Ожидание',
            deviation: 'Отклонение от плана',
            empty: 'Активных рейсов нет',
            saved: 'Порядок сохранён и защищён от пересчёта',
            loading: 'Загрузка',
            km: 'км',
            arrival: 'Прибытие',
            lateBy: 'опоздание {minutes} мин',
            lateByHours: 'опоздание {hours} ч {minutes} мин',
            windowTo: 'до {to}',
            windowFrom: 'с {from}',
            windowRange: 'с {from} до {to}',
            statuses: {
                planned: 'Запланирован',
                in_progress: 'В пути',
                completed: 'Завершён',
                cancelled: 'Отменён',
            },
        },
        journal: {
            title: 'Журнал аудита',
            when: 'Время',
            who: 'Кто',
            event: 'Событие',
            object: 'Объект',
            details: 'Подробности',
            more: 'Показать ещё',
            empty: 'Записей не найдено',
            total: 'Всего',
            tech: 'технические данные',
            techHide: 'скрыть',
            today: 'Сегодня',
            yesterday: 'Вчера',
            last7: '7 дней',
            pickDates: 'Выбрать даты',
            whoLabel: 'Кто',
            whoAny: 'Все',
            orderSearch: 'Номер заказа',
            categories: {
                all: 'Все',
                orders: 'Заказы',
                routes: 'Курьеры и маршруты',
                sessions: 'Входы и сессии',
                settings: 'Пользователи и настройки',
            },
            actors: {
                ct: 'Интеграция CT',
                system: 'Система',
            },
            objects: {
                order: 'Заказ',
                shift: 'Смена',
                task: 'Маршрут',
                driver: 'Курьер',
                driver_session: 'Сессия курьера',
                user: 'Пользователь',
                parameter: 'Параметр',
                ct_request: 'Запрос CT',
                embed_session: 'Встраивание',
                directory: 'Поиск курьера',
            },
            events: {
                web_login: 'Вход в кабинет',
                order_created: 'Заказ получен из CT',
                order_created_manual: 'Заказ создан вручную',
                order_updated: 'Заказ обновлён из CT',
                coordinates_set: 'Координаты заданы вручную',
                status_changed: 'Статус изменён',
                assigned: 'Назначен курьер',
                distribution_planned: 'Маршрут пересчитан',
                route_edited_by_driver: 'Курьер изменил порядок точек',
                stops_reordered: 'Диспетчер изменил порядок точек',
                reassigned: 'Точка передана другому курьеру',
                session_revoked: 'Сессия курьера завершена',
                shift_created: 'Курьер начал смену',
                modes_changed: 'Изменены отделы пользователя',
                courier_action: 'Действие курьера',
                permission_denied: 'Отказ в доступе',
                access_denied: 'Отклонён запрос CT',
                parameter_changed: 'Изменён параметр',
                message_created: 'Обращение CT к курьеру',
                message_replied: 'Курьер ответил на обращение',
                code_issued: 'Выдан код встраивания',
                session_opened: 'Открыто встраивание',
                data_accessed: 'Просмотр через встраивание',
                object_mismatch: 'Встраивание запросило чужой объект',
                driver_created: 'Добавлен курьер',
                installation_switch: 'Переход в другое направление',
                admin_bootstrapped: 'Создан первый администратор',
                lookup_missed: 'Номер курьера не найден',
            },
            text: {
                serviceClient: 'служебный клиент',
                ip: 'IP {ip}',
                forced: 'принудительно',
                reason: 'причина: {reason}',
                reasons: {
                    departed_to_recipient: 'курьер выехал к получателю',
                    run_loaded: 'рейс загружен',
                    deadline_forecast: 'прогноз не укладывается в срок',
                    departure_delay: 'задержка выезда',
                },
                stops: ['точка', 'точки', 'точек'],
                noLate: 'опозданий нет',
                late: 'опозданий: {count}',
                triggerDriver: 'по запросу курьера',
                triggerUrgent: 'срочный заказ',
                toEnd: 'точка перенесена в конец',
                toStart: 'точка перенесена в начало',
                toPosition: 'точка перенесена на {position}-е место',
                targetAt: 'привезти к {time}',
                anotherDevice: 'вход с другого устройства',
                geolocation: 'геолокация',
                notifications: 'уведомления',
                batterySaver: 'экономия батареи',
                yes: 'да',
                no: 'нет',
                zone: 'зона «{zone}»',
                fieldZone: 'зона',
                fieldNumber: 'номер CT',
                fieldPickup: 'точка отправления',
                fieldMode: 'режим',
                signatureMismatch: 'неверная подпись',
                noDepartments: 'без отделов',
                courierKinds: {
                    pickup: 'забрал заказ',
                    arrive: 'прибыл на точку',
                    deliver: 'вручил заказ',
                    fail: 'не доставил',
                    pickup_failed: 'товар не выдали',
                },
            },
        },
        templates: {
            title: 'Шаблоны СМС',
            code: 'Точка',
            language: 'Язык',
            body: 'Текст',
            segments: 'Сегментов',
            length: 'Символов',
            enabled: 'Включён',
            save: 'Сохранить',
            saved: 'Шаблон сохранён',
            hint: 'На кириллице сегмент — 70 символов: длинный текст превращает каждое сообщение в два-три',
            placeholders: 'Подстановки: {{number}}, {{link}}, {{window}}',
            points: {
                order_accepted: 'Заказ принят',
                window_assigned: 'Назначено окно доставки',
                picked_up: 'Курьер забрал заказ',
                courier_arrived: 'Курьер прибыл',
                delivered: 'Заказ доставлен',
                rescheduled: 'Доставка перенесена',
                cancelled: 'Заказ отменён',
            },
        },
        live: {
            connected: 'Живо',
            reconnecting: 'Переподключение',
            polling: 'Только опрос',
            updatedAgo: 'обновлено',
            mapUnavailable: 'Ключ карты не задан: курьеры показаны списком',
            staleWarning: 'Позиция устарела',
        },
        common: {
            orders: 'Заказы',
            settings: 'Настройки',
            brand: 'Zenta TMS',
            moreSections: 'Ещё',
            user: 'Пользователь',
            search: 'Поиск по номеру заказа или телефону',
            filters: 'Фильтры',
            reset: 'Сбросить',
            apply: 'Применить',
            cancel: 'Отмена',
            close: 'Закрыть',
            confirm: 'Подтвердить',
            reasonRequired: 'Укажите причину',
            loading: 'Загрузка',
            empty: 'Данных нет',
            emptyFiltered: 'По фильтру ничего не найдено',
            error: 'Произошла ошибка',
            forbidden: 'Раздел недоступен для вашей роли',
            departmentForbidden: 'Заказ относится к другому отделу: у вас к нему нет доступа',
            logout: 'Выйти',
            theme: 'Тема',
            themeDark: 'Включить тёмную тему',
            themeLight: 'Включить светлую тему',
            installation: 'Направление',
            departments: 'Отделы',
            switchFailed: 'Не удалось перейти в другое направление',
            of: 'из',
        },
    },
    /** Шаги инструкции по энергосбережению (FR-645). */
    battery: {
        miui: {
            autostart: 'Включите автозапуск',
            noRestrictions: 'Выберите «Без ограничений» по батарее',
            lockRecents: 'Закрепите приложение в списке задач',
        },
        emui: {
            manualLaunch: 'Переведите запуск на ручное управление',
            protectedApp: 'Добавьте в защищённые приложения',
        },
        oneui: {
            sleepingApps: 'Исключите из «Спящих приложений»',
            optimisation: 'Исключите из оптимизации батареи',
        },
        coloros: {
            autostart: 'Включите автозапуск',
            background: 'Разрешите работу в фоне',
        },
        android: {
            optimisation: 'Отключите оптимизацию батареи',
        },
        ios: {
            backgroundRefresh: 'Включите фоновое обновление',
            lowPowerMode: 'Отключите режим энергосбережения',
        },
    },
};
//# sourceMappingURL=ru.js.map